import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Box, Button, Stack } from "@mui/material";
import Replay from "@mui/icons-material/Replay";

import { PaymentLoadingContent, RegistrationStepPageLayout } from "components";
import { api, OnlinePaymentStatus } from "api";
import { useConfirmationQueries } from "../Payment/usePaymentQueries";

export const PaymentCallback = () => {
  const { t } = useTranslation();
  const { guid } = useParams<{ guid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    paymentContext: context,
    examDetails,
    isContextError,
  } = useConfirmationQueries(guid);

  const { mutate: checkStatus, isError: isCheckStatusError } = useMutation({
    mutationFn: async () => {
      const result = await api.customers.checkPaymentStatus(guid!);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["paymentContext", guid] });
      navigate(`/confirmation/${guid}`, { replace: true });
    },
  });

  const hasCheckedRef = useRef(false);

  // ── Check status when context loads ─────────────────────────────────────
  useEffect(() => {
    if (!context || hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    if (context.paymentStatus === OnlinePaymentStatus.Successful) {
      navigate(`/confirmation/${guid}`, { replace: true });
    } else {
      checkStatus();
    }
  }, [context, navigate, guid, checkStatus]);

  if (!guid || isContextError) {
    return <Alert severity="error">{t("common.generalError")}</Alert>;
  }

  return (
    <RegistrationStepPageLayout
      subTitle={t("payment.title")}
      selectedExam={examDetails}
      stepIndex={0}
      completed
      backToHome
      hideStepper
      paymentStatus={context?.paymentStatus}
      examCardPlaceholder
    >
      {isCheckStatusError ? (
        <Stack sx={{ gap: 2 }}>
          <Alert severity="error">{t("common.generalError")}</Alert>
          <Box>
            <Button
              variant="contained"
              onClick={() => checkStatus()}
              startIcon={<Replay />}
            >
              {t("payment.retry")}
            </Button>
          </Box>
        </Stack>
      ) : (
        <PaymentLoadingContent
          icon="lock"
          title={t("payment.callback.title")}
          subtitle={t("payment.callback.subtitle")}
          body={t("payment.callback.body")}
        />
      )}
    </RegistrationStepPageLayout>
  );
};
